/**
 * `?wm=` token — the awake-side "friendly fence" for paid members.
 *
 * Format: a url-safe base64 string that decodes to:
 *   { v: 1, e: "member@email", p: "prc_…", iat: <unix>, exp: <unix> }
 *
 * No HMAC — this is an obfuscation-grade fence, designed to be retired the
 * moment Memberstack's custom-domain cookie SSO works reliably. Forging a
 * token only grants free loom pulls, not access to anything sensitive.
 *
 * On page load we:
 *   1. Read `?wm=` from the URL if present
 *   2. Decode + validate (v, exp, qualifying plan)
 *   3. Persist to sessionStorage so refresh/in-tab nav keeps the access
 *   4. Strip `?wm=` from the visible URL (cleaner UX, no token bookmarking)
 *
 * `getStoredWmToken()` is used by `useMemberAccess` to grant member-state
 * synchronously, before the async Memberstack check resolves.
 */

const QUALIFYING_PRICE_IDS = new Set([
  "prc_1-club-monthly-txnv03iu",
  "prc_1-annual-n2170ed3",
  // Free comp tier for The Weave's team / co-founders.
  "pln_weave-team-np3a09sc",
]);

const STORAGE_KEY = "weave:wm-token:v1";
const TOKEN_VERSION = 1;

function nowSeconds() {
  return Math.floor(Date.now() / 1000);
}

function decodeUrlSafeBase64Json(raw) {
  try {
    let b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const json = atob(b64);
    const payload = JSON.parse(json);
    if (typeof payload !== "object" || !payload) return null;
    return payload;
  } catch {
    return null;
  }
}

function isValidPayload(payload) {
  if (!payload) return false;
  if (payload.v !== TOKEN_VERSION) return false;
  if (typeof payload.exp !== "number") return false;
  if (payload.exp <= nowSeconds()) return false; // expired
  if (!payload.p || !QUALIFYING_PRICE_IDS.has(payload.p)) return false;
  return true;
}

function readStored() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw);
    if (!isValidPayload(payload)) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

function writeStored(payload) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota/disabled — token is also still in the URL until we strip it */
  }
}

function stripTokenFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("wm")) return;
    url.searchParams.delete("wm");
    const next = url.pathname + (url.search ? url.search : "") + url.hash;
    window.history.replaceState({}, document.title, next);
  } catch {
    /* old browsers without history API — leave the param visible */
  }
}

/**
 * Reads `?wm=` from the current URL once. If valid, persists to
 * sessionStorage, strips the param from the URL, and returns the payload.
 * Returns null if absent / invalid / expired.
 */
export function consumeWmTokenFromUrl() {
  if (typeof window === "undefined") return null;
  let raw;
  try {
    raw = new URL(window.location.href).searchParams.get("wm");
  } catch {
    return null;
  }
  if (!raw) return null;

  const payload = decodeUrlSafeBase64Json(raw);
  // Always strip the URL after consumption, even on invalid tokens —
  // there's no reason to keep a malformed `?wm=` in the address bar.
  stripTokenFromUrl();

  if (!isValidPayload(payload)) return null;
  writeStored(payload);
  return payload;
}

export function getStoredWmToken() {
  return readStored();
}

export function clearWmToken() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Convenience: on first call, consume URL token if present, otherwise read
 * from storage. Returns the active payload (or null) for this tab session.
 */
export function getActiveWmToken() {
  const fromUrl = consumeWmTokenFromUrl();
  if (fromUrl) return fromUrl;
  return getStoredWmToken();
}
